import React, { FC, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box, Dialog, DialogActions, DialogContent, Grid, Tab, Tabs } from "@mui/material";
import { CommentOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";

import { QUERY_KEYS } from "consts";
import { createEstimates, getSiteById, UnitDescription, updateEstimate } from "api";
import { checkNullValue, toUSNumber } from "utils";
import { useAmplitude, useNotify } from "hooks";

import { EstimatesModalProps, FormData } from "./types";
import { createInitialFormValues, getModalTitle } from "./helpers";
import { FormFields } from "./formFields";
import { fieldStyles } from "./styles";
import { ActionButtons } from "./actionButtons";
import { EstimateComments } from "./comments";

export const EstimatesModal: FC<EstimatesModalProps> = ({
  isOpen,
  onClose,
  allowedValues,
  siteData,
  editEstimates,
  isEditNotes,
  onEstimatesDataUpdate,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { trackEvent } = useAmplitude();

  const { mutateAsync: createEstimateRequest } = useMutation(createEstimates);
  const { mutateAsync: updateEstimateRequest } = useMutation(updateEstimate);

  const [currentTab, setCurrentTab] = useState<string>("form");
  const [notesError, setNotesError] = useState<boolean>(false);
  const [availableUnitDescriptions, setAvailableUnitDescriptions] = useState<UnitDescription[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [commentsTitle, setCommentsTitle] = useState<string>("Comments");

  const isEditMode = !!editEstimates;
  const isCommentsMode = currentTab === "comments";
  const modalTitle = getModalTitle(!!isEditNotes, isEditMode);

  const initialFormValues = createInitialFormValues(editEstimates, isEditMode);

  const { control, handleSubmit, reset, watch, setValue } = useForm<FormData>({
    mode: "onChange",
    defaultValues: initialFormValues,
  });

  const watchedUtilityType = watch("utilityName");
  const watchedEstimateUsageUnit = watch("estimateUsageUnit");
  const watchedEstimateType = watch("estimateType");
  const watchedEstimateUsage = watch("estimateUsage");
  const watchedEstimateHeadCount = watch("estimateHeadCount");

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  const handleClose = () => {
    reset(initialFormValues);
    setNotesError(false);
    onClose();
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    const baseRequestData = {
      estimateName: checkNullValue(data.estimateName),
      estimateType: checkNullValue(data.estimateType),
      days: 21,
      estimateUsage: Number(data.estimateUsage) || 0,
      estimateHeadCount: checkNullValue(data.estimateHeadCount),
      estimateUsageUnit: checkNullValue(data.estimateUsageUnit),
      estimateStartDate: checkNullValue(data.estimateStartDate),
      estimateEndDate: checkNullValue(data.estimateEndDate),
      scope: checkNullValue(data.scope),
      frequency: "MONTHLY",
      siteId: siteData?.id || 0,
      utilityTypeId: allowedValues?.utilityTypes?.find((ut: any) => ut.name.toLowerCase() === data.utilityName.toLowerCase())?.id || 0,
      estimateSource: checkNullValue(data.estimateSource),
      notes: checkNullValue(data.notes),
      id: checkNullValue(editEstimates?.id),
    };

    try {
      if (isEditMode) {
        await updateEstimateRequest(baseRequestData, {
          onError: () => notify.error(`${!isEditNotes ? "Some error has happened while editing Estimates" : "Some error has happened while updating notes!"}`),
          onSuccess: () => notify.success(`${!isEditNotes ? "Estimates successfully edited" : "Notes successfully Updated!"}`),
        });
      } else {
        if (siteData?.enabled) {
          trackEvent("Added Estimate", "Site Management - Active");
        };

        await createEstimateRequest(baseRequestData, {
          onSuccess: () => {
            notify.success("Estimates successfully created. The estimates will be generated on the 5th of every month");
            onClose();
          },
        });
      };

      if (siteData && onEstimatesDataUpdate) {
        getSiteById(siteData?.id)
          .then((newSiteData) => onEstimatesDataUpdate(newSiteData))
          .catch(() => notify.error("Error fetching site data"));
      }

      // Invalidate queries
      queryClient.invalidateQueries([QUERY_KEYS.ESTIMATES, QUERY_KEYS.SITES, siteData?.id]);

      handleClose();
    } catch (error: any) {
      notify.error(error?.response?.data && typeof error?.response?.data === "string" ? error?.response?.data : "Some error has happened while adding Estimates!");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form and tab when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      const newInitialValues = createInitialFormValues(editEstimates, isEditMode);
      reset(newInitialValues);
      setCurrentTab("form");
    }
  }, [isOpen, editEstimates, reset, isEditMode]);

  // Update available unit descriptions when utility type changes
  useEffect(() => {
    if (watchedUtilityType) {
      const selectedUtilityTypeInfo = allowedValues?.utilityTypes?.find((ut: any) => ut.name.toLowerCase() === watchedUtilityType.toLowerCase());
      if (selectedUtilityTypeInfo) {
        setAvailableUnitDescriptions(selectedUtilityTypeInfo.unitDescription || []);
      }
    }
  }, [watchedUtilityType, allowedValues]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
        }
      }}
    >
      <Tabs value={currentTab} onChange={handleTabChange} aria-label="Estimates Tabs">
        <Tab label={modalTitle} value="form" />
        {editEstimates && !isEditNotes && (<Tab label={commentsTitle} value="comments" icon={<CommentOutlined />} iconPosition="start" sx={{ minHeight: "48px" }} />)}
      </Tabs>
      {currentTab === "form" && (
        <Box>
          <DialogContent sx={fieldStyles.dialogContent}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <FormFields
                  control={control}
                  watch={watch}
                  setValue={setValue}
                  allowedValues={allowedValues}
                  notesError={notesError}
                  setNotesError={setNotesError}
                  isNotesOnlyMode={!!isEditNotes}
                  availableUnitDescriptions={availableUnitDescriptions}
                />
              </Grid>
              {watchedEstimateUsageUnit && watchedUtilityType && watchedEstimateType && (
                <Box
                  sx={{
                    backgroundColor: "#F2F2F2",
                    display: "flex",
                    mt: 2,
                    p: 2,
                  }}
                >
                  <span style={{ fontSize: "14px" }}>
                    <b>
                      You are estimating {toUSNumber(watchedEstimateUsage ?? 0)}{" "}
                      {watchedEstimateUsageUnit} of {watchedUtilityType} per day per {watchedEstimateType}.
                      <br />
                      {watchedEstimateType} of {watchedEstimateHeadCount} is assumed.
                    </b>
                  </span>
                </Box>
              )}
              {!isEditNotes && (
                <Box sx={{
                  width: "100%",
                  mt: 2,
                  pl: 2,
                }}>
                  <span style={{ fontSize: "14px" }}>
                    Note: 21 days per month is assumed.
                    <br />
                    New estimates will be generated on the 5th of each month
                  </span>
                </Box>
              )}
            </form>
          </DialogContent>
        </Box>
      )}
      {currentTab === "comments" && (
        <DialogContent>
          <EstimateComments
            siteId={siteData?.id || 0}
            estimateId={editEstimates?.id ?? 0}
            setCommentsTitle={setCommentsTitle}
          />
        </DialogContent>
      )}
      <DialogActions>
        <ActionButtons
          editEstimates={editEstimates}
          isNotesOnlyMode={!!isEditNotes}
          isCommentsMode={isCommentsMode}
          isSubmitting={isSubmitting}
          onCancel={handleClose}
          onSave={handleSubmit(onSubmit)}
        />
      </DialogActions>
    </Dialog>
  );
};
